import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { scrollIntoView } from '#src/utils/scrollIntoView.ts'

/*
 * Product spec (modeled after Linear.app inbox):
 * Highlight persists until the user clicks anywhere on the page.
 * This supports multi-tab: opening a link in a background tab keeps
 * the highlight visible until the user switches to that tab and clicks,
 * confirming they've seen the highlighted element.
 * capture: true ensures the click is caught even if children call stopPropagation.
 *
 * Scrolls to and highlights a ref'd element when `highlighted` transitions
 * from false → true. Animates out via click-to-dismiss or when `highlighted`
 * transitions back to false from upstream.
 *
 * An IntersectionObserver re-scrolls if async content above the target shifts
 * layout after the initial scroll. The observer waits for the element to stay
 * visible for 300ms before disconnecting, so late-arriving content that causes
 * additional layout shifts still triggers a re-scroll. Once disconnected, the
 * observer won't fight user scrolling.
 *
 * TODO: remove the IntersectionObserver once sidebar loading is refactored to
 * use Suspense boundaries — rendering all content at once eliminates layout
 * shifts and makes a single scrollIntoView sufficient.
 */
export function useScrollHighlight(highlighted: boolean): {
  ref: RefObject<HTMLDivElement | null>
  highlightClassName: string | undefined
} {
  const ref = useRef<HTMLDivElement | null>(null)
  const [highlight, setHighlight] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (highlighted) {
      setHighlight(true)
      scrollIntoView(ref.current, { block: 'center', behavior: 'smooth' })

      const controller = new AbortController()

      let settleTimeout: ReturnType<typeof setTimeout> | undefined

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            settleTimeout = setTimeout(() => observer.disconnect(), 300)
          } else {
            clearTimeout(settleTimeout)
            if (ref.current) {
              scrollIntoView(ref.current, {
                block: 'center',
                behavior: 'smooth',
              })
            }
          }
        },
        { threshold: 0.5 }
      )

      requestAnimationFrame(() => {
        if (ref.current) {
          observer.observe(ref.current)
        }
      })

      document.addEventListener(
        'click',
        () => {
          setHighlight((prev) => (prev === true ? false : prev))
        },
        { once: true, capture: true, signal: controller.signal }
      )

      return () => {
        clearTimeout(settleTimeout)
        observer.disconnect()
        controller.abort()
      }
    }
    /* Only transition to "out" if we were previously "in". The functional
       update avoids a stale-closure dep on `highlight` and naturally no-ops
       on first mount (prev is undefined, not true). */
    setHighlight((prev) => (prev === true ? false : prev))
    return undefined
  }, [highlighted])

  const highlightClassName = getHighlightClassName(highlight)
  return useMemo(() => ({ ref, highlightClassName }), [highlightClassName])
}

function getHighlightClassName(
  highlight: boolean | undefined
): string | undefined {
  if (highlight === true) {
    return 'animate-comment-border-highlight-in'
  }
  return highlight === false
    ? 'animate-comment-border-highlight-out'
    : undefined
}
